import React, { useState } from "react";
import { Checkbox, Modal, Input } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import * as kioskApi from "../services/kioskApi";
import "./AgreementsPage.scss";
import Loader from "components/customAntd/Loader";

interface Agreement {
  id: string;
  name: string;
  content?: string; // Optional - may not be loaded yet
}

interface AgreementsPageProps {
  agreements: Agreement[];
  agreedAgreements: string[];
  onAgreementChange: (agreementId: string, checked: boolean) => void;
  token: string;
  locationId: number;
  agreementsAccept?: number;
}

const AgreementsPage: React.FC<AgreementsPageProps> = ({
  agreements,
  agreedAgreements,
  onAgreementChange,
  token,
  agreementsAccept,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState<Agreement | null>(
    null,
  );
  const [loadingContent, setLoadingContent] = useState(false);
  const [agreementContent, setAgreementContent] = useState<string>("");

  const handleViewAgreement = async (agreement: Agreement) => {
    setSelectedAgreement(agreement);
    setModalOpen(true);

    // If content is already available (from getWorkflowByVisitorType), use it
    if (agreement.content) {
      setAgreementContent(agreement.content);
      return;
    }

    // Otherwise, fetch content from API (default agreements from getLocationAgreements)
    try {
      setLoadingContent(true);

      const documentData = await kioskApi.getDocumentContent(
        parseInt(agreement.id),
        token,
      );

      const content =
        documentData?.content || t("selfSignIn.agreements.noContent");
      setAgreementContent(content);
    } catch (error) {
      setAgreementContent(t("selfSignIn.agreements.loadFailed"));
    } finally {
      setLoadingContent(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedAgreement(null);
    setAgreementContent("");
  };

  return (
    <div className="workflow-container">
      <div className="message">{t("selfSignIn.agreements.title")}</div>

      <div className="agreements-list">
        {agreements.map((agreement) => (
          <div key={agreement.id} className="agreement-item">
            <Checkbox
              checked={agreedAgreements.includes(agreement.id)}
              onChange={(e) =>
                onAgreementChange(agreement.id, e.target.checked)
              }
              disabled={agreementsAccept !== 1}
            >
              <span className="agreement-name">{agreement.name}</span>
            </Checkbox>
            <FileTextOutlined
              className="document-icon"
              onClick={() => handleViewAgreement(agreement)}
            />
          </div>
        ))}
      </div>

      <Modal
        title={selectedAgreement?.name}
        open={modalOpen}
        onCancel={handleCloseModal}
        footer={null}
        centered
      >
        {loadingContent ? (
          <Loader />
        ) : (
          <Input.TextArea
            value={agreementContent}
            rows={20}
            readOnly
            style={{ resize: "none" }}
          />
        )}
      </Modal>
    </div>
  );
};

export default AgreementsPage;

// Handler function to be called by parent component
export const handleAgreementsNext = async (
  agreedAgreements: string[],
  totalAgreements: number,
  agreementsAccept?: number,
): Promise<{ success: boolean }> => {
  // Check if agreements are required (agreementsAccept === 1)
  if (agreementsAccept === 1) {
    if (agreedAgreements.length < totalAgreements) {
      throw new Error("selfSignIn.agreements.notAllAccepted");
    }
  }

  return { success: true };
};
